import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AppState,
  FullSidebar,
  LoaderPayload,
  LoaderState,
  NavigationData,
  RolesAndPermissions,
  SidebarState,
  ToastState,
} from "./models";

import {
  LANGUAGE_CODE,
  TOAST_AUTO_HIDE_DURATION,
} from "../../../helpers/constants";

import { IdNameList } from "../../../api/services/common/models";
import {
  PageContentApiResponse,
  PageContentResponse,
} from "../../../api/services/pageContent/models";

const initialState: AppState = {
  preferredLanguage: LANGUAGE_CODE.EN_US,
  rolesAndPermissions: {
    permissionPages: [],
    permissions: [],
    roles: [],
  },
  loader: {
    global: { isVisible: false, message: "" },
  } as LoaderState,
  sidebar: {
    isOpen: false,
    destinationPath: "",
  } as SidebarState,
  toast: {
    open: false,
    message: "",
    type: "success", // 'error' | 'warning' | 'info' | 'success',
    autoHideDuration: TOAST_AUTO_HIDE_DURATION,
  } as ToastState,
  siteCorePageContent: {
    Title: "",
    Components: {},
  } as PageContentResponse,
  siteCorePageContentLoading: false,
  navigationSiteCorePageContent: {
    Title: "",
    Components: {},
  } as PageContentResponse,
  fullSidebar: {
    isOpen: true,
    showCollapseButton: true,
  } as FullSidebar,
  isDataModifiedCurrentPage: false,
  navigationData: {
    onNavigationClicked: false,
    navigationPath: "",
  },
  currencyList: [] as IdNameList[],
};

const appStateSlice = createSlice({
  name: "appStateSlice",
  initialState,
  reducers: {
    setUserPreferredLanguage: (state, action: PayloadAction<string>) => {
      state.preferredLanguage = action.payload;
    },
    setUserRolesAndPermissions: (
      state,
      action: PayloadAction<RolesAndPermissions>,
    ) => {
      state.rolesAndPermissions = action.payload;
    },
    toggleLoader: (
      state,
      action: PayloadAction<
        | Record<string, LoaderPayload> // For specific loader
        | LoaderPayload // For global loader
      >,
    ) => {
      // Case 1: If action.payload has a key like "program"
      if (
        Object.keys(action.payload).length === 1 &&
        !action.payload.hasOwnProperty("isVisible")
      ) {
        const [key, { isVisible, message }] = Object.entries(action.payload)[0];
        state.loader[key] = { isVisible, message };
      } else {
        // Case 2: If no key, use 'global' as the default
        const global = action.payload as LoaderPayload;
        state.loader["global"] = {
          isVisible: global.isVisible,
          message: global.message ?? "",
        };
      }
    },

    toggleUnmountLoader: (state) => {
      state.loader = { ...initialState.loader };
    },
    toggleSideBar: (state, action: PayloadAction<SidebarState>) => {
      state.sidebar = {
        isOpen: action.payload.isOpen,
        destinationPath: action.payload?.destinationPath ?? "",
      };
    },
    toggleFullSidebar: (state, action: PayloadAction<boolean>) => {
      state.fullSidebar = {
        isOpen: action.payload,
        showCollapseButton: true,
      };
    },
    //for show/hide collapse button
    toggleFullSidebarWithCollapseButton: (
      state,
      action: PayloadAction<FullSidebar>,
    ) => {
      state.fullSidebar = {
        isOpen: action.payload.isOpen,
        showCollapseButton: action.payload.showCollapseButton,
      };
    },
    toggleToast: (state, action: PayloadAction<ToastState>) => {
      state.toast = {
        open: action.payload.open,
        message: action.payload.message,
        type: action.payload.type ?? "success",
        autoHideDuration:
          action.payload?.autoHideDuration ?? TOAST_AUTO_HIDE_DURATION,
      };
    },
    setSiteCorePageContentLoading: (state, action: PayloadAction<boolean>) => {
      const { payload } = action;
      state.siteCorePageContentLoading = payload;
    },
    setSiteCorePageContent: (
      state,
      action: PayloadAction<PageContentApiResponse>,
    ) => {
      const { payload } = action;
      if (payload && payload.Pages && Array.isArray(payload.Pages)) {
        state.siteCorePageContent = payload.Pages[0];
        const msg =
          payload.Pages[0]?.Components?.CommonComponent?.Labels?.find(
            (el: { Key: string; Phrase: string }) =>
              el.Key === "Common.LoadingPleaseWait",
          )?.Phrase || "";
        sessionStorage.setItem("loadingMessage", msg);
      } else {
        state.siteCorePageContent = initialState.siteCorePageContent;
      }
    },
    resetSiteCorePageContent: (state) => {
      state.siteCorePageContent = initialState.siteCorePageContent;
    },
    setNavigationSiteCorePageContent: (
      state,
      action: PayloadAction<PageContentApiResponse>,
    ) => {
      const { payload } = action;
      if (payload && payload.Pages && Array.isArray(payload.Pages)) {
        state.navigationSiteCorePageContent = payload.Pages[0];
      } else {
        state.navigationSiteCorePageContent =
          initialState.navigationSiteCorePageContent;
      }
    },
    setNavigationDataClicked: (
      state,
      action: PayloadAction<NavigationData>,
    ) => {
      state.navigationData = action.payload;
    },
    setIsDataModifiedCurrentPage: (state, action: PayloadAction<boolean>) => {
      state.isDataModifiedCurrentPage = action.payload;
    },
    setCurrencyList: (state, action: PayloadAction<IdNameList[]>) => {
      state.currencyList = action.payload;
    },
  },
});

export const {
  setUserPreferredLanguage,
  setUserRolesAndPermissions,
  toggleLoader,
  toggleUnmountLoader,
  toggleSideBar,
  toggleFullSidebar,
  toggleFullSidebarWithCollapseButton,
  toggleToast,
  setSiteCorePageContent,
  setSiteCorePageContentLoading,
  setNavigationSiteCorePageContent,
  resetSiteCorePageContent,
  setNavigationDataClicked,
  setIsDataModifiedCurrentPage,
  setCurrencyList,
} = appStateSlice.actions;

export default appStateSlice.reducer;
